'use client';

import { createContext, useContext, useCallback } from 'react';
import { UIToastProvider, useToast as useUIToast, type ToastOptions } from '@repo/ui/toast';

// ── Wrapper that keeps our showToast(type, title, message) API ───

type ToastType = 'success' | 'error' | 'info';

interface ToastContextValue {
    showToast: (type: ToastType, title: string, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
}

// ── Inner bridge component (has access to UIToast context) ───────

function ToastBridge({ children }: { children: React.ReactNode }) {
    const show = useUIToast();

    const showToast = useCallback((type: ToastType, title: string, message: string) => {
        const variantMap: Record<ToastType, ToastOptions['variant']> = {
            success: 'success',
            error: 'danger',
            info: 'info',
        };
        show({ variant: variantMap[type], title, message });
    }, [show]);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
        </ToastContext.Provider>
    );
}

// ── Export: drop-in replacement using UIToast design system ───────

export function ToastProvider({ children }: { children: React.ReactNode }) {
    return (
        <UIToastProvider>
            <ToastBridge>
                {children}
            </ToastBridge>
        </UIToastProvider>
    );
}
